import { VNominationDocumentTypes, type VNominationDocumentType } from "@constants/certificates";
import cloneDeep from "lodash/cloneDeep";
import lodashSet from "lodash/set";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { NominationDocumentFields } from "../../utils";
import { FORM_FIELDS } from "../constants";
import type { VesselNominationFormData } from "../schema/vesselNominationFormSchema";

interface VesselNominationFormMethods {
  setField<K extends keyof VesselNominationFormData>(
    key: K,
    val: VesselNominationFormData[K],
  ): void;
  setAll(data: Partial<VesselNominationFormData>): void;
  resetForm(): void;
}

export interface VesselNominationFormState
  extends VesselNominationFormData,
    VesselNominationFormMethods {}

const documentDefaults = Object.values(VNominationDocumentTypes).reduce(
  (acc, documentType) => {
    acc[documentType as VNominationDocumentType] = {
      [NominationDocumentFields.URL]: "",
      [NominationDocumentFields.DOCUMENT_NAME]: "",
      [NominationDocumentFields.TYPE]: "",
      [NominationDocumentFields.OTHER_SPECIFY]: "",
      [NominationDocumentFields.ISSUE_DATE]: null,
      [NominationDocumentFields.EXPIRY_DATE]: null,
    };
    return acc;
  },
  {} as Record<
    VNominationDocumentType,
    {
      [NominationDocumentFields.URL]: string;
      [NominationDocumentFields.DOCUMENT_NAME]: string;
      [NominationDocumentFields.TYPE]: string;
      [NominationDocumentFields.OTHER_SPECIFY]: string;
      [NominationDocumentFields.ISSUE_DATE]: Date | null;
      [NominationDocumentFields.EXPIRY_DATE]: Date | null;
    }
  >,
);

export const defaultValues: VesselNominationFormData = {
  [FORM_FIELDS.VESSEL_ID]: "",
  [FORM_FIELDS.RTA]: null,
  [FORM_FIELDS.ETA]: null,
  [FORM_FIELDS.CALL_SIGN]: "",
  [FORM_FIELDS.PREVIOUS_VESSEL_NAME]: "",
  [FORM_FIELDS.LOA]: undefined,
  [FORM_FIELDS.BALLAST]: undefined,
  [FORM_FIELDS.DRAUGHT_SUMMER]: undefined,
  [FORM_FIELDS.DRAUGHT_TROPICAL]: undefined,
  [FORM_FIELDS.ARRIVAL_FWD]: undefined,
  [FORM_FIELDS.ARRIVAL_MID]: undefined,
  [FORM_FIELDS.ARRIVAL_AFT]: undefined,
  [FORM_FIELDS.MAX_TRIM]: undefined,
  [FORM_FIELDS.DEPARTURE_FWD]: undefined,
  [FORM_FIELDS.DEPARTURE_MID]: undefined,
  [FORM_FIELDS.DEPARTURE_AFT]: undefined,
  [FORM_FIELDS.ARRIVAL_DRAFT]: undefined,
  [FORM_FIELDS.DEPARTURE_DRAFT]: undefined,
  [FORM_FIELDS.NET_TONNAGE]: undefined,
  [FORM_FIELDS.GROSS_TONNAGE]: undefined,
  [FORM_FIELDS.LIGHT]: undefined,
  [FORM_FIELDS.LOADED]: undefined,
  [FORM_FIELDS.ARRIVAL]: undefined,
  [FORM_FIELDS.DEPARTURE]: undefined,
  [FORM_FIELDS.SUMMER_DWT]: undefined,
  [FORM_FIELDS.TROPICAL_DWT]: undefined,
  [FORM_FIELDS.MANIFOLDS_SIZE]: "",
  [FORM_FIELDS.MANIFOLDS_NUMBER]: undefined,
  [FORM_FIELDS.MANIFOLDS_DISTANCE]: undefined,
  [FORM_FIELDS.MANIFOLDS_HEIGHT_GRAFTING]: undefined,
  [FORM_FIELDS.MANIFOLDS_HEIGHT_BALLAST]: undefined,
  [FORM_FIELDS.MANIFOLDS_HEIGHT_LOADED]: undefined,
  [FORM_FIELDS.MANIFOLDS_EXTREME_HEIGHT]: undefined,
  [FORM_FIELDS.MANIFOLDS_DISTANCE_BOW_BCM_STERN]: undefined,
  [FORM_FIELDS.MANIFOLDS_DISTANCE_BOW_SCM_STERN]: undefined,
  [FORM_FIELDS.MANIFOLDS_DISTANCE_INBOARD]: undefined,
  [FORM_FIELDS.TERMINAL_LOADING_ARM_CONNECTION]: "",
  [FORM_FIELDS.EXHAUST_GAS_CLEANING_SYSTEM]: "",
  [FORM_FIELDS.EXHAUST_GAS_CLEANING_SYSTEM_TYPE]: "",
  [FORM_FIELDS.CONFIRM_INERT_GAS_SYSTEM_FITTED]: "",
  [FORM_FIELDS.IS_INERT_GAS_SYSTEM_OPERATIONAL]: "",
  [FORM_FIELDS.VESSEL_CONDITION]: "",
  [FORM_FIELDS.CARGO_TYPE]: "",
  [FORM_FIELDS.CARGO_TYPE_OTHER]: "",
  [FORM_FIELDS.CARGO_QUANTITY_M3]: undefined,
  [FORM_FIELDS.CARGO_QUANTITY]: undefined,
  [FORM_FIELDS.PARCELS]: [],
  [FORM_FIELDS.FLASH_POINT]: undefined,
  [FORM_FIELDS.SULPHUR]: undefined,
  [FORM_FIELDS.MERCAPTAN]: undefined,
  [FORM_FIELDS.STOWED_CARGO_TEMPERATURE]: undefined,
  [FORM_FIELDS.HYDROGEN_SULPHIDE]: undefined,
  [FORM_FIELDS.RVP]: undefined,
  [FORM_FIELDS.COLOUR_ASTM]: "",
  [FORM_FIELDS.BENZENE_CONTENT]: undefined,
  [FORM_FIELDS.ARE_HIGH_H2S_TANKS_ADEQUATE]: "",
  [FORM_FIELDS.AVG_PRESSURE_CARGO_TANKS_ARRIVAL]: undefined,
  [FORM_FIELDS.MAX_PRESSURE_MANIFOLDS]: undefined,
  [FORM_FIELDS.IS_CRUDE_OIL_WASHING_OPERATION_PLANNED]: "",
  [FORM_FIELDS.MOORING_FWD]: undefined,
  [FORM_FIELDS.MOORING_MID]: undefined,
  [FORM_FIELDS.MOORING_AFT]: undefined,
  [FORM_FIELDS.HAS_MOORING_DETAILS_AND_DEFICIENCIES]: "",
  [FORM_FIELDS.MOORING_DETAILS_AND_DEFICIENCIES]: "",
  [FORM_FIELDS.HAS_ANY_DEFECTS_HULL_MACHINERY_EQUIPMENT]: "",
  [FORM_FIELDS.ANY_DEFECTS_HULL_MACHINERY_EQUIPMENT]: "",
  [FORM_FIELDS.HAS_AREA_WITH_PROTRUSION]: "",
  [FORM_FIELDS.AREA_WITH_PROTRUSION]: "",
  [FORM_FIELDS.LAST_SIRE_INSPECTION]: undefined,
  [FORM_FIELDS.ISSUING_AUTHORITY]: "",
  [FORM_FIELDS.LAST_PSC_INSPECTION]: undefined,
  [FORM_FIELDS.PLACE]: "",
  [FORM_FIELDS.LAST_PORT]: "",
  [FORM_FIELDS.NEXT_PORT]: "",
  [FORM_FIELDS.SECURITY_LEVEL]: "",
  [FORM_FIELDS.PAQ_DOCUMENT]: {
    url: "",
    documentName: "",
  },
  [FORM_FIELDS.CONFIRMATION]: false,
  [FORM_FIELDS.DOCUMENTS]: documentDefaults,
  [FORM_FIELDS.REMARKS]: "",
  [FORM_FIELDS.ADDITIONAL_NOMINATION_DOCUMENTS]: [],
  [FORM_FIELDS.PENDING_UPLOADS]: 0,
};

const NUMERIC_FIELDS = [
  FORM_FIELDS.LOA,
  FORM_FIELDS.BALLAST,
  FORM_FIELDS.DRAUGHT_SUMMER,
  FORM_FIELDS.DRAUGHT_TROPICAL,
  FORM_FIELDS.ARRIVAL_AFT,
  FORM_FIELDS.ARRIVAL_MID,
  FORM_FIELDS.ARRIVAL_FWD,
  FORM_FIELDS.MAX_TRIM,
  FORM_FIELDS.DEPARTURE_AFT,
  FORM_FIELDS.DEPARTURE_MID,
  FORM_FIELDS.DEPARTURE_FWD,
  FORM_FIELDS.ARRIVAL_DRAFT,
  FORM_FIELDS.DEPARTURE_DRAFT,
  FORM_FIELDS.NET_TONNAGE,
  FORM_FIELDS.GROSS_TONNAGE,
  FORM_FIELDS.LIGHT,
  FORM_FIELDS.LOADED,
  FORM_FIELDS.ARRIVAL,
  FORM_FIELDS.DEPARTURE,
  FORM_FIELDS.SUMMER_DWT,
  FORM_FIELDS.TROPICAL_DWT,
  FORM_FIELDS.MANIFOLDS_NUMBER,
  FORM_FIELDS.MANIFOLDS_DISTANCE,
  FORM_FIELDS.MANIFOLDS_HEIGHT_GRAFTING,
  FORM_FIELDS.MANIFOLDS_HEIGHT_BALLAST,
  FORM_FIELDS.MANIFOLDS_HEIGHT_LOADED,
  FORM_FIELDS.MANIFOLDS_EXTREME_HEIGHT,
  FORM_FIELDS.MANIFOLDS_DISTANCE_BOW_BCM_STERN,
  FORM_FIELDS.MANIFOLDS_DISTANCE_BOW_SCM_STERN,
  FORM_FIELDS.MANIFOLDS_DISTANCE_INBOARD,
  FORM_FIELDS.CARGO_QUANTITY_M3,
  FORM_FIELDS.CARGO_QUANTITY,
  FORM_FIELDS.FLASH_POINT,
  FORM_FIELDS.SULPHUR,
  FORM_FIELDS.MERCAPTAN,
  FORM_FIELDS.STOWED_CARGO_TEMPERATURE,
  FORM_FIELDS.HYDROGEN_SULPHIDE,
  FORM_FIELDS.RVP,
  FORM_FIELDS.BENZENE_CONTENT,
  FORM_FIELDS.AVG_PRESSURE_CARGO_TANKS_ARRIVAL,
  FORM_FIELDS.MAX_PRESSURE_MANIFOLDS,
  FORM_FIELDS.MOORING_FWD,
  FORM_FIELDS.MOORING_MID,
  FORM_FIELDS.MOORING_AFT,
];

export function transformNumberToString(data: Record<string, any>) {
  return Object.fromEntries(
    Object.entries(data).map(([key, value]) =>
      NUMERIC_FIELDS.includes(key as any)
        ? [key, value !== undefined && value !== null ? value.toString() : ""]
        : [key, value],
    ),
  );
}

export const useVesselNominationFormStore = create<VesselNominationFormState>()(
  persist(
    (set: (fn: (state: VesselNominationFormState) => VesselNominationFormState) => void) => ({
      ...defaultValues,
      setField: (key, val) =>
        set((state) => {
          const newState = cloneDeep(state);
          lodashSet(newState, key, val);
          return newState;
        }),
      setAll: (data) => set((s) => ({ ...s, ...data })),
      resetForm: () => set((state) => ({ ...state, ...defaultValues })),
    }),
    { name: "vessel-nomination-form" },
  ),
);
